import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Box,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';

const Blog = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);

    try {
      const parser = new DOMParser();
      const res = await fetch("https://www.ilsole24ore.com/rss/economia--lavoro.xml");
      const text = await res.text();
      const xml = parser.parseFromString(text, "text/xml");

      const items = xml.querySelectorAll("item");
      const parsedArticles = Array.from(items).map((item) => {
        const getText = (tag) => item.querySelector(tag)?.textContent?.trim() || "";
        const getCDATA = (tag) => {
          const el = item.querySelector(tag);
          if (!el) return "";
          const cdataNode = Array.from(el.childNodes).find(n => n.nodeType === Node.CDATA_SECTION_NODE);
          return cdataNode ? cdataNode.data.trim() : el.textContent?.trim() || "";
        };

        const stripHTML = (html) => {
          const div = document.createElement("div");
          div.innerHTML = html;
          return div.textContent || "";
        };

        return {
          title: getCDATA("title"),
          description: stripHTML(getCDATA("description")),
          url: getText("link"),
          image: item.querySelector("enclosure")?.getAttribute("url") || "",
          publishedAt: getText("pubDate"),
          source: { name: "Il Sole 24 Ore" },
        };
      });


      if (parsedArticles.length === 0) {
        setError("Nessun articolo trovato.");
        setArticles([]);
      } else {
        setArticles(parsedArticles);
      }
    } catch (err) {
      console.error("Errore durante il parsing RSS:", err);
      setError("Errore nel caricamento delle notizie.");
      setArticles([]);
    }

    setLoading(false);
  };


  useEffect(() => {
    fetchNews();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "Data sconosciuta";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Data sconosciuta";
      }
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return date.toLocaleDateString('it-IT', options);
    } catch (e) {
      console.error("Error formatting date:", dateString, e);
      return "Data sconosciuta";
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Notizie sul Lavoro in Italia
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Notizie aggiornate sul mercato del lavoro e la consulenza in Italia
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {!loading && articles.length > 0 && (
        <Grid container spacing={3}>
          {articles.map((article, index) => {
            const articleKey = article.url ? article.url : `${article.title}-${index}`;

            return (
              <Grid item xs={12} sm={6} md={4} key={articleKey}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea
                    component="a"
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <CardMedia
                      component="img"
                      height="180"
                      image={article.image || `https://source.unsplash.com/random/800x600/?lavoro,ufficio,${index}`}
                      alt={article.title || 'Immagine articolo'}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {article.source?.name || "Fonte Sconosciuta"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(article.publishedAt)}
                        </Typography>
                      </Box>
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {article.title || 'Titolo non disponibile'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(article.description || article.content || "Nessuna descrizione disponibile").substring(0, 150)}
                        {((article.description || article.content || "").length > 150) ? "..." : ""}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {loading && articles.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      <Snackbar
        open={error}
        autoHideDuration={3000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="success" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Blog;
